import React from "react";
import { useIntl } from "react-intl";
import classNames from "classnames";
import { ButtonTextAlign, WebChatColorProps } from "@42maru/webchat";
import { NumberInput, SwitchInput } from "@/component/input";
import Tooltip from "@/component/Tooltip";
import { ReactComponent as AlignLeftIcon } from "@/assets/svgs/align_left.svg";
import { ReactComponent as AlignCenterIcon } from "@/assets/svgs/align_center.svg";
import { ReactComponent as AlignRightIcon } from "@/assets/svgs/align_right.svg";

import { WebchatThemeFormProps } from ".";
import { WebchatRadioGroup, WebchatRadioGroupOption } from "../component";


const ChatWindowInputGroup: React.FC<WebchatThemeFormProps> = (props) => {
    const { theme, legend, onChange } = props;

    const intl = useIntl();

    const onChangeFontSize = (key: keyof WebChatColorProps, value: string) => {
        onChange({ ...theme, colors: { ...theme.colors, [key]: value } as WebChatColorProps });
    };

    return (
        <fieldset className="flex flex-col gap-4">
            <legend className="sr-only">
                {legend}
            </legend>

            <fieldset className="flex flex-col gap-5">
                <legend className="field-label-bold">
                    {intl.formatMessage({ id: "b0054" })}
                </legend>
                <div className="label-input-grid-[152] gap-x-2 gap-y-2">
                    <div className="label-input-grid-label flex items-center gap-1">
                        <label htmlFor="webchat-setting-font-size" className="field-label">
                            {intl.formatMessage({ id: "i09023" })}
                        </label>
                        <Tooltip message={intl.formatMessage({ id: "i09028" })} />
                    </div>
                    <NumberInput id="webchat-setting-font-size"
                                 width={96}
                                 min={10} max={20}
                                 value={parseInt(theme.colors?.fontSizeNormal ?? "10")}
                                 onChange={e => onChangeFontSize("fontSizeNormal", `${e.target.value}px`)}
                    />

                    <label htmlFor="webchat-setting-msg-delay" className="label-input-grid-label field-label">
                        {intl.formatMessage({ id: "i09088" })}
                    </label>
                    <NumberInput id="webchat-setting-msg-delay"
                                 width={96}
                                 step={0.05}
                                 min={0}
                                 value={theme.messageDelay}
                                 onChange={e => onChange({ ...theme, messageDelay: Math.round(Number(e.target.value) * 100) / 100 })}
                    />
                </div>
            </fieldset>

            <hr className="-mx-2 mb-2 border-black-200" />

            <fieldset className="flex flex-col gap-5">
                <legend className="field-label-bold">
                    {intl.formatMessage({ id: "b0023" })}
                </legend>
                <div className="label-input-grid-[152] gap-x-2 gap-y-5">
                    <div className="label-input-grid-label flex items-center gap-1">
                        <label htmlFor="webchat-setting-btn-font-size" className="field-label">
                            {intl.formatMessage({ id: "i09025" })}
                        </label>
                        <Tooltip message={intl.formatMessage({ id: "i09028" })} />
                    </div>
                    <NumberInput id="webchat-setting-btn-font-size"
                                 width={96}
                                 min={10} max={20}
                                 value={parseInt(theme.colors?.fontSizeButton ?? "10")}
                                 onChange={e => onChangeFontSize("fontSizeButton", `${e.target.value}px`)}
                    />

                    <div id="webchat-button-text-align"
                         className="label-input-grid-label field-label">
                        {intl.formatMessage({ id: "i09026" })}
                    </div>
                    <WebchatRadioGroup name="webchat-button-text-align"
                                       aria-labelledby="webchat-button-text-align"
                                       options={BUTTON_TEXT_ALIGN_OPTIONS}
                                       selectedValue={theme.buttonTextAlign}
                                       onChange={v => onChange({ ...theme, buttonTextAlign: v })}
                    />
                </div>
            </fieldset>

            <hr className="-mx-2 mb-2 border-black-200" />

            <fieldset className="flex flex-col gap-5">
                <legend className="field-label-bold">
                    {intl.formatMessage({ id: "b0055" })}
                </legend>
                <div className="label-input-grid-[152] gap-x-2 gap-y-5">
                    <div className="label-input-grid-label flex items-center gap-1">
                        <label htmlFor="webchat-setting-title-font-size" className="field-label">
                            {intl.formatMessage({ id: "i09024" })}
                        </label>
                        <Tooltip message={intl.formatMessage({ id: "i09028" })} />
                    </div>
                    <NumberInput id="webchat-setting-title-font-size"
                                 width={96}
                                 min={10} max={20}
                                 value={parseInt(theme.colors?.fontSizeTitle ?? "10")}
                                 onChange={e => onChangeFontSize("fontSizeTitle", `${e.target.value}px`)}
                    />

                    <div id="webchat-carousel-button-text-align"
                         className="label-input-grid-label field-label">
                        {intl.formatMessage({ id: "i09031" })}
                    </div>
                    <WebchatRadioGroup name="webchat-carousel-button-text-align"
                                       aria-labelledby="webchat-carousel-button-text-align"
                                       options={BUTTON_TEXT_ALIGN_OPTIONS}
                                       selectedValue={theme.carouselButtonTextAlign}
                                       onChange={v => onChange({ ...theme, carouselButtonTextAlign: v })}
                    />

                    <div id="webchat-carousel-button-border-radius"
                         className="label-input-grid-label field-label">
                        {intl.formatMessage({ id: "i09032" })}
                    </div>
                    <WebchatRadioGroup name="webchat-carousel-button-border-radius"
                                       aria-labelledby="webchat-carousel-button-border-radius"
                                       options={BUTTON_BORDER_RADIUS_OPTIONS}
                                       selectedValue={theme.carouselButtonBorderRadius}
                                       onChange={v => onChange({ ...theme, carouselButtonBorderRadius: v })}
                    />
                </div>
            </fieldset>

            <hr className="-mx-2 mb-2 border-black-200" />

            <fieldset className="pb-4 label-input-grid-[152] gap-x-2 gap-y-2">
                <legend className="sr-only">
                    {intl.formatMessage({ id: "i09037" })}
                </legend>
                <label htmlFor="webchat-prevent-scroll"
                       className="label-input-grid-label field-label"
                >
                    {intl.formatMessage({ id: "i09037" })}
                </label>
                <SwitchInput id="webchat-prevent-scroll"
                             defaultValue={theme.preventWelcomeScroll}
                             onSwitch={value => {
                                 onChange({ ...theme, preventWelcomeScroll: value });
                             }}
                />

                <div id="webchat-use-read-more"
                     className="label-input-grid-label flex items-center gap-1"
                >
                    <div className="field-label">
                        {intl.formatMessage({ id: "i09027" })}
                    </div>
                    <Tooltip message={intl.formatMessage({ id: "i09022" })} />
                </div>
                <SwitchInput aria-labelledby="webchat-use-read-more"
                             defaultValue={theme.useReadMore}
                             onSwitch={value => {
                                 onChange({ ...theme, useReadMore: value });
                             }}
                />
                <div className={classNames(
                         "webchat-setting-card-grid-indent",
                         "transition-disclosure",
                         theme.useReadMore ? "open !-mt-2" : "closed"
                     )}
                >
                    <label htmlFor="webchat-setting-read-more-line"
                           className="ml-2 label-input-grid-label input-label"
                    >
                        {intl.formatMessage({ id: "i09020" })}
                    </label>
                    <NumberInput id="webchat-setting-read-more-line"
                                 width={96}
                                 min={1}
                                 value={theme.readMoreLine ?? 0}
                                 onChange={e => onChange({ ...theme, readMoreLine: parseInt(e.target.value) })}
                    />
                </div>

                <label htmlFor="webchat-show-quick-replies"
                       className="label-input-grid-label field-label"
                >
                    {intl.formatMessage({ id: "i09018" })}
                </label>
                <SwitchInput id="webchat-show-quick-replies"
                             defaultValue={theme.showAllQuickReplies}
                             onSwitch={value => {
                                 onChange({ ...theme, showAllQuickReplies: value });
                             }}
                />

                <label htmlFor="webchat-use-feedback"
                       className="label-input-grid-label field-label"
                >
                    {intl.formatMessage({ id: "i09036" })}
                </label>
                <SwitchInput id="webchat-use-feedback"
                             defaultValue={theme.useFeedbackCollector}
                             onSwitch={value => {
                                 onChange({ ...theme, useFeedbackCollector: value });
                             }}
                />
            </fieldset>
        </fieldset>
    );
};

const BUTTON_BORDER_RADIUS_OPTIONS: WebchatRadioGroupOption<string>[] = [
    { value: "22px 22px 22px 22px", labelKey: "b0026" },
    { value: "2px 2px 2px 2px", labelKey: "b0027" }
];

const BUTTON_TEXT_ALIGN_OPTIONS: WebchatRadioGroupOption<ButtonTextAlign>[] = [
    { value: "left", labelIcon: <AlignLeftIcon className="size-4.5" /> },
    { value: "center", labelIcon: <AlignCenterIcon className="size-4.5" /> },
    { value: "right", labelIcon: <AlignRightIcon className="size-4.5" /> }
];

export default ChatWindowInputGroup;
